import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type {
  BanquetteBaseTemplate,
  CreateBanquetteBaseTemplate,
  UpdateBanquetteBaseTemplate,
} from '@estimator/shared';

export function useBanquetteBaseTemplates() {
  return useQuery({
    queryKey: ['banquette-base-templates'],
    queryFn: async () => {
      const { data } = await apiClient.get<BanquetteBaseTemplate[]>('/banquette-base-templates');
      return data;
    },
  });
}

export function useBanquetteBaseTemplate(id: string | null) {
  return useQuery({
    queryKey: ['banquette-base-templates', id],
    queryFn: async () => {
      const { data } = await apiClient.get<BanquetteBaseTemplate>(
        `/banquette-base-templates/${id}`,
      );
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateBanquetteBaseTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateBanquetteBaseTemplate) => {
      const { data } = await apiClient.post<BanquetteBaseTemplate>(
        '/banquette-base-templates',
        body,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banquette-base-templates'] });
    },
  });
}

export function useUpdateBanquetteBaseTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateBanquetteBaseTemplate & { id: string }) => {
      const { data } = await apiClient.patch<BanquetteBaseTemplate>(
        `/banquette-base-templates/${id}`,
        body,
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['banquette-base-templates'] });
      queryClient.invalidateQueries({ queryKey: ['banquette-base-templates', vars.id] });
    },
  });
}

export function useDeleteBanquetteBaseTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/banquette-base-templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banquette-base-templates'] });
    },
  });
}
